import * as React from "react";
import * as ReactDOM from "react-dom";
import { Button, makeStyles, ThemeProvider, useMediaQuery, useTheme } from "@material-ui/core";
import { MUIDialog, Theme } from "../../entryes/panel/MUI";
import { tl } from "../../utils";
import { AccessControlProvider } from "../../context/AccessControl.context";
import { useAccessControl } from "../../hooks/useAccessControl";
import useLocalStorage from "../../hooks/useLocalStorage";

interface CourseCaptionProps {
  courseId: string;
  desktopCaption: string;
  mobileCaption: string;
}

declare global {
  interface Window {
    react_render_courseCaption: (nodeId: string, data: CourseCaptionProps) => void;
  }
}

export const CourseCaption: React.FC<CourseCaptionProps> = ({courseId, desktopCaption, mobileCaption}) => {
  const [showRemoveDialog, setShowRemoveDialog] = React.useState(false);
  const [showDefaultCaption, setShowDefaultCaption] = useLocalStorage("showDefaultCaption", true);

  const theme = useTheme();
  const classes = useStyles();
  const acl = useAccessControl();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  
  const defaultCaptionLaterButtonHandler = () => {
    setShowRemoveDialog(true);
  }

  const changeCaptionHandler = () => {
    window.open(`/panel/courses/edit?course_id=${courseId}&expanded=true`, '_blank')
  }

  const defaultCaption = (
    <div className={classes.defaultCaption}>
      <div className={classes.defaultCaptionMain}>
        <span className={classes.defaultCaptionHeader}>
          {tl("У вашего курса может быть красивая обложка")}
        </span>
        <span className={classes.defaultCaptionDescription}>
          {tl("Просто добавьте ее в настройках курса")}
        </span>
      </div>
      <div className={classes.defaultCaptionAdditional}>
        <div className={classes.defaultCaptionButtonContainer}>
          <Button className={classes.defaultCaptionLaterButton} onClick={defaultCaptionLaterButtonHandler}>
            {tl("Добавлю позже")}
          </Button>
          <Button className={classes.defaultCaptionAddButton} onClick={changeCaptionHandler}>
            {tl("Добавить обложку")}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!isMobile && !desktopCaption && acl.check("courses", ["update"]) && showDefaultCaption &&
        defaultCaption
      }
      {!isMobile && desktopCaption &&
        <div className={classes.captionContainer}>
          <img src={`/media/2280/${desktopCaption}`} className={classes.desktopCaption} alt="Course caption"/>
          {acl.check("courses", ["update"]) &&
            <Button className={classes.captionChangeButton} onClick={changeCaptionHandler}>
              {tl("Изменить обложку")}
            </Button>
          }
        </div>
      }
      {isMobile && !mobileCaption && acl.check("courses", ["update"]) &&  showDefaultCaption &&
        defaultCaption
      }
      {isMobile && mobileCaption &&
        <div className={classes.captionContainer}>
          <img src={`/media/1300/${mobileCaption}`} className={classes.mobileCaption} alt="Course caption"/>
          {acl.check("courses", ["update"]) &&
            <Button className={classes.captionChangeButton} onClick={changeCaptionHandler}>
              {tl("Изменить обложку")}
            </Button>
          }
        </div>
      }
      <MUIDialog
        actions={[
          [{
            label: tl("Убрать"),
            btnProps: {
              color: "default",
              onClick: () => {
                setShowRemoveDialog(false);
                setShowDefaultCaption(false);
              }
            }
          }],
          [{
            label: tl("Оставить"),
            btnProps: {
              color: "primary",
              onClick: () => {
                setShowRemoveDialog(false);
              }
            }
          }]
        ]}
        open={showRemoveDialog}
        title={tl("Убрать шапку-напоминание?")}
      >
        <p>{tl("Графическая обложка курса помогает транслировать ученикам ваш бренд, создавать впечатление более дорогого и качественного продукта")}</p>
        <p>{tl("Запомните, что добавление обложки находится в меню «Настройки» Курса и вы сможете добавить ее позже")}</p>
      </MUIDialog>
    </>
  )
}

const useStyles = makeStyles(theme => ({
  desktopCaption: {
    width: "100%",
    borderRadius: 4,

    [theme.breakpoints.down("xs")]: {
      borderRadius: "unset",
    },
  },
  mobileCaption: {
    width: "100%",
  },
  defaultCaption: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    boxSizing: "border-box",
    height: 200,
    padding: "30px 32px 30px 100px",
    minWidth: 360,
    maxHeight: 300,
    margin: "3px 0 16px 0",
    overflow: "hidden",
    
    borderRadius: 4,
    background: "linear-gradient(90deg, #55C6F1 0.37%, #3285ED 99.93%)",

    [theme.breakpoints.down("sm")]: {
      padding: "16px 32px 16px 32px",
    },

    [theme.breakpoints.down("xs")]: {
      flex: 1,
      flexDirection: "column",
      
      maxHeight: 390,

      borderRadius: "unset",
    },
  },
  defaultCaptionMain: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  defaultCaptionHeader: {
    fontFamily: 'SF UI Text',
    fontSize: 41,
    lineHeight: 1.2,
    fontWeight: 700,
    color: theme.palette.common.white,

    [theme.breakpoints.down("md")]: {
      fontSize: 31,
    },

    [theme.breakpoints.down("sm")]: {
      fontSize: 25,
    },

    [theme.breakpoints.down("xs")]: {
      fontSize: 21,
    },
  },
  defaultCaptionDescription: {
    fontFamily: 'SF UI Text',
    fontSize: 25,
    lineHeight: 1.2,
    fontWeight: 400,
    color: theme.palette.common.white,

    [theme.breakpoints.down("md")]: {
      fontSize: 20,
    },

    [theme.breakpoints.down("sm")]: {
      fontSize: 18,
    },

    [theme.breakpoints.down("xs")]: {
      fontSize: 18,
    },
  },
  defaultCaptionAdditional: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
  },
  defaultCaptionButtonContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,

    [theme.breakpoints.down("xs")]: {
      justifyContent: "flex-end",
    },
  },
  defaultCaptionLaterButton: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.common.white,
    borderRadius: 2,
    color: theme.palette.common.white,
    whiteSpace: "nowrap",

    [theme.breakpoints.down("xs")]: {
      fontSize: 14,
    },
  },
  defaultCaptionAddButton: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "transparent",
    borderRadius: 2,
    background: theme.palette.common.white,
    color: theme.palette.primary.main,
    whiteSpace: "nowrap",

    [theme.breakpoints.down("xs")]: {
      fontSize: 14,
    },

    "&:hover": {
      backgroundColor: theme.palette.grey[200],
    }
  },
  captionContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    maxHeight: 300,
    margin: "3px 0 16px 0",
    overflow: "hidden",

    [theme.breakpoints.down("xs")]: {
      maxHeight: 390,
    },

    "&:hover": {
      "& > $captionChangeButton": {
        display: "block",
      }
    }
  },
  captionChangeButton: {
    display: "none",
    position: "absolute",
    bottom: 30,
    right: 32,

    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "transparent",
    borderRadius: 2,
    background: theme.palette.common.white,
    color: theme.palette.primary.main,
    whiteSpace: "nowrap",

    "&:hover": {
      backgroundColor: theme.palette.grey[200],
    }
  },
}));

window.react_render_courseCaption = (nodeId: string, data: CourseCaptionProps) => {
  ReactDOM.render(
    <AccessControlProvider course_id={data.courseId}>
      <ThemeProvider theme={Theme}>
        <CourseCaption {...data} />
      </ThemeProvider>
    </AccessControlProvider>,
    document.getElementById(nodeId)
  )
}